/* eslint-disable */
import { isFeatureEnabled } from '@motionelements/core/src/components/mixins/feature-flag.mixin.js';
import $store from '@/store/index';

/**
 * Inline playback on mobile (required for iOS Safari).
 * Without playsinline, mobile browsers may take over native fullscreen.
 *
 * @param {HTMLVideoElement} videoEl
 */
export function applyInlinePlayback(videoEl) {
  if (!videoEl) {
    return;
  }
  videoEl.playsInline = true;
  videoEl.setAttribute('playsinline', '');
  videoEl.setAttribute('webkit-playsinline', '');
}

/**
 * Vertical clips use contain so the full frame stays inside the layout box.
 *
 * @param {HTMLVideoElement} videoEl
 * @param {{ horizontal?: 'contain' | 'cover' }} options
 */
const unbindVideoOrientationObjectFit = (videoEl) => {
  if (videoEl && videoEl.__meOrientationFitAbort) {
    videoEl.__meOrientationFitAbort.abort();
    videoEl.__meOrientationFitAbort = null;
    videoEl.__meOrientationFitBound = false;
  }
};

export function bindVideoOrientationObjectFit(videoEl, options = {}) {
  const horizontalFit = options.horizontal === 'cover' ? 'cover' : 'contain';

  if (!videoEl) {
    return;
  }

  const apply = () => {
    const { videoWidth, videoHeight } = videoEl;
    if (!videoWidth || !videoHeight) {
      return;
    }
    const isVertical = videoHeight > videoWidth;
    videoEl.style.objectFit = isVertical ? 'contain' : horizontalFit;
  };

  unbindVideoOrientationObjectFit(videoEl);
  const abortController = new AbortController();
  videoEl.__meOrientationFitAbort = abortController;
  videoEl.__meOrientationFitBound = true;
  videoEl.addEventListener('loadedmetadata', apply, { signal: abortController.signal });

  apply();
}

const getStore = () => (typeof $store === 'function' ? $store() : $store);

let containerEl = null;
let videoElement = null;
let elementData = null;
let clickHandler = null;
/** @type {AbortController|null} */
let playbackEventsAbort = null;
/** @type {boolean} search-style thumbnail-first vs product detail preview-first */
let preferThumbnailSource = false;

const pickPosterUrl = (el) => {
  if (!el) {
    return '';
  }
  if (el.thumbnail && el.thumbnail.url) {
    return el.thumbnail.url;
  }
  if (el.thumbnails && el.thumbnails.jpg && el.thumbnails.jpg.url) {
    return el.thumbnails.jpg.url;
  }
  if (el.previews && el.previews.jpg && el.previews.jpg.url) {
    return el.previews.jpg.url;
  }
  return '';
};

const pickVideoSrc = (el) => {
  if (!el) {
    return null;
  }
  let url = null;
  if (preferThumbnailSource) {
    if (el.thumbnails && el.thumbnails.mp4 && el.thumbnails.mp4.url) {
      url = el.thumbnails.mp4.url;
    } else if (el.previews && el.previews.mp4 && el.previews.mp4.url) {
      url = el.previews.mp4.url;
    }
  } else if (el.previews && el.previews.mp4 && el.previews.mp4.url) {
    url = el.previews.mp4.url;
  } else if (el.thumbnails && el.thumbnails.mp4 && el.thumbnails.mp4.url) {
    url = el.thumbnails.mp4.url;
  }
  if (!url && el.previews && el.previews.webm && el.previews.webm.url) {
    url = el.previews.webm.url;
  }
  if (!url) {
    return null;
  }
  if (url.lastIndexOf('.mp4') === -1 && url.lastIndexOf('.webm') === -1) {
    return null;
  }
  return url;
};

const resolveVideoElement = (container) => {
  if (!container) {
    return null;
  }
  if (container.tagName === 'VIDEO') {
    return container;
  }
  return container.querySelector('video');
};

const setStoreStatus = (type, bool) => {
  getStore().commit('videoPlayer/setStatus', { type, bool });
};

const unbindVideoEvents = () => {
  if (playbackEventsAbort) {
    playbackEventsAbort.abort();
    playbackEventsAbort = null;
  }
};

const bindVideoEvents = (video) => {
  if (!video) {
    return;
  }
  unbindVideoEvents();
  playbackEventsAbort = new AbortController();
  const { signal } = playbackEventsAbort;
  video.__meNativeEventsBound = true;
  video.addEventListener('loadeddata', () => {
    setStoreStatus('ready', true);
  }, { signal });
  video.addEventListener('playing', () => {
    setStoreStatus('playing', true);
  }, { signal });
  video.addEventListener('pause', () => {
    setStoreStatus('pause', true);
  }, { signal });
};

const ensureVideoSrc = () => {
  if (!videoElement || !elementData) {
    return;
  }
  const src = pickVideoSrc(elementData);
  if (!src) {
    return;
  }
  const currentAttrSrc = videoElement.getAttribute('src') || '';
  if (currentAttrSrc !== src) {
    videoElement.src = src;
  }
};

const attemptPlay = (preferUnmuted = true) => {
  if (!videoElement) {
    return;
  }
  ensureVideoSrc();
  if (!videoElement.getAttribute('src')) {
    return;
  }
  videoElement.muted = !preferUnmuted;
  const playPromise = videoElement.play();
  if (!playPromise || typeof playPromise.then !== 'function') {
    return;
  }
  playPromise.catch(() => {
    if (!preferUnmuted) {
      return;
    }
    videoElement.muted = true;
    const mutedPlay = videoElement.play();
    if (mutedPlay && typeof mutedPlay.catch === 'function') {
      mutedPlay.catch(() => {});
    }
  });
};

const applyNativeControls = (video, enabled) => {
  if (!video) {
    return;
  }
  if (enabled) {
    video.controls = true;
    video.setAttribute('controls', '');
    video.setAttribute('controlsList', 'nodownload');
  } else {
    video.controls = false;
    video.removeAttribute('controls');
    video.removeAttribute('controlsList');
  }
};

const onContainerClick = () => {
  if (!videoElement || videoElement.controls) {
    return;
  }
  if (videoElement.paused) {
    attemptPlay(true);
  } else {
    videoElement.pause();
  }
};

const bindContainerClick = () => {
  if (!containerEl || containerEl.tagName === 'VIDEO' || clickHandler) {
    return;
  }
  clickHandler = onContainerClick;
  containerEl.addEventListener('click', clickHandler);
};

const unbindContainerClick = () => {
  if (containerEl && clickHandler) {
    containerEl.removeEventListener('click', clickHandler);
    clickHandler = null;
  }
};

const nativeVideoPlayer = {
  isEnabled() {
    return isFeatureEnabled('product_preview_native_video');
  },

  init(dom, el, options = {}) {
    const nextVideo = resolveVideoElement(dom);
    if (!nextVideo) {
      return;
    }

    preferThumbnailSource = !!options.preferThumbnailSource;
    containerEl = dom;
    elementData = el;
    videoElement = nextVideo;

    if (el && el.id) {
      getStore().commit('videoPlayer/setPlayerId', el.id);
    }

    bindVideoEvents(videoElement);
    applyInlinePlayback(videoElement);
    bindVideoOrientationObjectFit(videoElement, { horizontal: 'contain' });
    videoElement.loop = true;
    const showControls = !!options.controls;
    const preloadMode = options.preload === 'metadata' ? 'metadata' : 'none';
    const primeSource = showControls || !!options.autoplay || preloadMode === 'metadata';
    videoElement.preload = preloadMode;

    const posterUrl = pickPosterUrl(el);
    videoElement.poster = posterUrl;
    if (posterUrl) {
      videoElement.setAttribute('poster', posterUrl);
    } else {
      videoElement.removeAttribute('poster');
    }

    videoElement.removeAttribute('src');
    if (primeSource) {
      ensureVideoSrc();
    }
    // load() with no src clears the poster on iOS; metadata preload keeps poster until play.
    if (primeSource && videoElement.getAttribute('src')) {
      videoElement.load();
    }

    applyNativeControls(videoElement, showControls);
    unbindContainerClick();
    if (!showControls) {
      bindContainerClick();
    }

    setStoreStatus('ready', true);

    if (options.autoplay) {
      attemptPlay(options.autoplayUnmuted !== false);
    } else if (preloadMode === 'metadata') {
      videoElement.pause();
    } else {
      nativeVideoPlayer.stop();
    }
  },

  play() {
    attemptPlay(true);
  },

  stop() {
    if (!videoElement) {
      return;
    }
    videoElement.pause();
    videoElement.currentTime = 0;
  },

  pause() {
    if (!videoElement) {
      return;
    }
    videoElement.pause();
  },

  volume(level = 0) {
    if (!videoElement) {
      return;
    }
    videoElement.volume = level;
  },

  muted(toggle = false) {
    if (!videoElement) {
      return;
    }
    videoElement.muted = toggle;
  },

  seek(sec) {
    if (!videoElement) {
      return;
    }
    videoElement.currentTime = sec;
  },

  speed(s) {
    if (!videoElement) {
      return;
    }
    videoElement.playbackRate = s;
  },

  unload() {
    if (!videoElement) {
      return;
    }
    videoElement.pause();
    videoElement.removeAttribute('src');
    videoElement.load();
  },

  shutdown() {
    nativeVideoPlayer.unload();
    unbindContainerClick();
    unbindVideoEvents();
    if (videoElement) {
      unbindVideoOrientationObjectFit(videoElement);
      applyNativeControls(videoElement, false);
      videoElement.__meNativeEventsBound = false;
    }
    containerEl = null;
    videoElement = null;
    elementData = null;
    preferThumbnailSource = false;
  },
};

// Search grid hover previews (ProductPreviewVideo): autoplay policy requires muted play() without a user gesture.
// After click/tap/key on the document, hover clips may honor search mute pref.
// Wired via installMediaUserGesture() on Search.vue and resolveHoverPreviewMuted() on cards.
let hoverPreviewMediaUserGesture = false;
const hoverPreviewMediaUserGestureListeners = new Set();
const HOVER_PREVIEW_GESTURE_EVENTS = ['pointerdown', 'keydown', 'touchstart'];
export const SEARCH_HOVER_MUTE_CONTROL_SELECTOR = '[data-search-hover-mute]';

export function isSearchHoverMuteControl(target) {
  if (!target || typeof target.closest !== 'function') {
    return false;
  }
  return Boolean(target.closest(SEARCH_HOVER_MUTE_CONTROL_SELECTOR));
}

const notifyMediaUserGesture = () => {
  hoverPreviewMediaUserGestureListeners.forEach((listener) => {
    listener();
  });
};

// Whether the page has had a gesture that allows unmuting hover preview video.
export function hasMediaUserGesture() {
  if (hoverPreviewMediaUserGesture) {
    return true;
  }
  if (typeof navigator !== 'undefined') {
    const userActivation = navigator.userActivation;
    if (userActivation && userActivation.hasBeenActive) {
      hoverPreviewMediaUserGesture = true;
      return true;
    }
  }
  return false;
}

export function markMediaUserGesture() {
  if (hoverPreviewMediaUserGesture) {
    return;
  }
  hoverPreviewMediaUserGesture = true;
  notifyMediaUserGesture();
}

// Subscribe when installMediaUserGesture() records the first document interaction.
export function onMediaUserGesture(listener) {
  if (typeof listener !== 'function') {
    return () => {};
  }
  hoverPreviewMediaUserGestureListeners.add(listener);
  return () => {
    hoverPreviewMediaUserGestureListeners.delete(listener);
  };
}

// Effective muted state for search hover <video> (template :muted and post-play() unmute).
export function resolveHoverPreviewMuted({
  isPlaying,
  userWantsMuted,
  allowAudibleHover,
  hasUserGesture,
}) {
  if (!isPlaying) {
    return true;
  }
  if (userWantsMuted) {
    return true;
  }
  if (!allowAudibleHover) {
    return true;
  }
  const gesture = typeof hasUserGesture === 'boolean'
    ? hasUserGesture
    : hasMediaUserGesture();
  return !gesture;
}

// Search mute toolbar icon: matches audible hover state (pref muted or no document gesture yet).
export function isHoverPreviewToolbarMuted(userWantsMuted, hasUserGesture) {
  return resolveHoverPreviewMuted({
    isPlaying: true,
    userWantsMuted,
    allowAudibleHover: true,
    hasUserGesture,
  });
}

// Call once per search session; returns teardown for beforeDestroy.
export function installMediaUserGesture() {
  if (typeof document === 'undefined') {
    return () => {};
  }

  const activate = (event) => {
    if (event && isSearchHoverMuteControl(event.target)) {
      return;
    }
    markMediaUserGesture();
    removeListeners();
  };

  const removeListeners = () => {
    HOVER_PREVIEW_GESTURE_EVENTS.forEach((event) => {
      document.removeEventListener(event, activate, true);
    });
  };

  if (hasMediaUserGesture()) {
    return removeListeners;
  }

  HOVER_PREVIEW_GESTURE_EVENTS.forEach((event) => {
    document.addEventListener(event, activate, true);
  });

  return removeListeners;
}

export default nativeVideoPlayer;
